import React from 'react';
import PropTypes from 'prop-types';

import {
  ELEVATED_PROMO_PRICE_ROW_LAYOUT,
  LIMITED_TIME_OFFER_BANNER_VARIANT,
} from '../../../constants/campaignPromoVariants';
import { SUBSCRIPTION_TYPES } from '../../../constants/subscriptions';
import LimitedTimeOfferBanner from '../../CampaignPromo/LimitedTimeOfferBanner';
import { ElevatedPromoPriceRow } from '../../CampaignPromo/PromoPriceDisplay';

/**
 * MobileComparisonTable component displays a mobile-optimized comparison
 * of features available in standard vs elevated profiles
 *
 * @param {Object} props
 * @param {ReadonlyArray<import('../constants').ComparisonFeature>} props.features - Array of features to compare
 * @param {boolean} [props.showCampaignPricing]
 * @param {string} [props.className] - Additional CSS classes
 */
const MobileComparisonTable = ({
  features = [],
  showCampaignPricing = false,
  useWideDesktopComparisonBreakpoint = false,
  className = '',
}) => {
  const priceRowHeightClass = showCampaignPricing
    ? 'min-h-[123px]'
    : 'h-[90px]';

  const rootVisibilityClass = useWideDesktopComparisonBreakpoint
    ? 'flex min-[1280px]:hidden'
    : 'flex md:hidden';

  return (
    <div
      className={`relative z-10 ${rootVisibilityClass} w-full flex-col rounded-lg bg-white ${
        showCampaignPricing ? 'mt-12 overflow-visible' : ''
      } ${className}`}
    >
      {/* Header */}
      <div
        className={`flex items-stretch ${
          showCampaignPricing ? 'overflow-visible' : 'items-center'
        }`}
      >
        <div className="flex w-1/2 items-center justify-center rounded-tl-lg bg-gray-20 p-3 text-sm font-semibold leading-5 text-gray-90">
          STANDARD
        </div>
        <div className="relative w-1/2 overflow-visible">
          {showCampaignPricing && (
            <div
              className="absolute left-1/2 top-[-52px] z-20 -translate-x-1/2"
              data-qa-target="limited-time-offer-badge-anchor-mobile"
            >
              <LimitedTimeOfferBanner
                variant={LIMITED_TIME_OFFER_BANNER_VARIANT.COMPACT}
              />
            </div>
          )}
          <div className="flex h-full items-center justify-center rounded-tr-lg bg-linear-to-r from-blue-50 to-blue-30 p-3 text-sm font-semibold leading-5 text-white">
            ELEVATED
          </div>
        </div>
      </div>

      {/* Price Row */}
      <div className="flex h-10 items-center justify-center border-b border-gray-20 py-2 text-sm font-semibold leading-5 text-deep-blue-70">
        Price
      </div>
      <div className={`flex items-stretch ${priceRowHeightClass}`}>
        <div className="flex w-1/2 flex-col items-center justify-center px-1">
          <span className="text-base font-semibold leading-6 text-gray-90">
            $0
          </span>
        </div>
        <div className="flex w-1/2 flex-col items-center justify-center gap-2 border-l border-blue-30 px-2">
          {showCampaignPricing ? (
            <ElevatedPromoPriceRow
              layout={ELEVATED_PROMO_PRICE_ROW_LAYOUT.SPLIT}
              stacked
            />
          ) : (
            <>
              <div className="flex items-center gap-1">
                <span className="text-base font-semibold text-gray-90">
                  {SUBSCRIPTION_TYPES.MONTHLY.displayPrice}
                </span>
                <span className="whitespace-nowrap text-sm font-normal leading-5 text-gray-90">
                  monthly
                </span>
              </div>
              <div className="h-[2px] w-10 bg-gray-20" />
              <div className="flex items-center gap-1">
                <span className="text-base font-semibold text-gray-90">
                  {SUBSCRIPTION_TYPES.ANNUAL.displayPrice}
                </span>
                <span className="whitespace-nowrap text-sm font-normal leading-5 text-gray-90">
                  annually
                </span>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Feature Rows */}
      {features.map((item) => (
        <React.Fragment key={item.feature}>
          <div className="flex h-10 items-center justify-center border-b border-t border-gray-20 py-2 text-sm font-semibold leading-5 text-deep-blue-70">
            {item.feature}
          </div>
          <div className="flex items-stretch">
            <div className="flex w-1/2 items-center justify-center p-4 text-base font-semibold">
              {item.standard ? (
                <img
                  src="/public/images/check-icon.svg"
                  alt="check-icon"
                  className="h-5 w-5 text-blue-30"
                />
              ) : (
                <span className="text-center text-xl font-light leading-5 text-deep-blue-70">
                  -
                </span>
              )}
            </div>
            <div className="flex w-1/2 items-center justify-center border-l border-blue-30 p-4">
              {item.elevated ? (
                <img
                  src="/public/images/check-icon.svg"
                  alt="check-icon"
                  className="h-5 w-5 text-blue-30"
                />
              ) : (
                <span className="text-center text-xl font-light leading-5 text-deep-blue-70">
                  -
                </span>
              )}
            </div>
          </div>
        </React.Fragment>
      ))}
    </div>
  );
};

MobileComparisonTable.propTypes = {
  features: PropTypes.arrayOf(
    PropTypes.shape({
      feature: PropTypes.string.isRequired,
      standard: PropTypes.bool.isRequired,
      elevated: PropTypes.bool.isRequired,
    }),
  ),
  showCampaignPricing: PropTypes.bool,
  useWideDesktopComparisonBreakpoint: PropTypes.bool,
  className: PropTypes.string,
};

export default MobileComparisonTable;
